import { Box, Flex, Text, useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import Select from "../../shared/components/Select";
import useApi from "../../shared/hooks/useApi";
import {
  HHAXPocItemId,
  PayerId,
  PlanOfCareItemId,
  PlanOfCareTypeId,
} from "../../shared/schema/schema";
import { formatErrorResponse } from "../../shared/utils/format-response-error";
import ErrorState from "./components/ErrorState";
import HhaxMappingRow from "./components/HhaxMappingRow";
import LoadingState from "./components/LoadingState";
import MappingTableShell from "./components/MappingTableShell";
import { bySectionThenCode } from "./evv-duty-code-mapping.utils";
import { DraftHHAXMappingMap, HHAXItemOption, PlanOfCareColumn } from "./hhax-poc-mapping.types";

/**
 * Pass `payerId` to lock the screen to a single payer - that is how the payer settings entry
 * point opens it. Left out, the screen offers a payer picker, which is how the EVV duty code
 * mapping tab opens it.
 */
export default function HhaxPocMappingTable(props: { payerId?: PayerId }) {
  const { api, queries } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();

  const payersQuery = useQuery(queries.evvDutyCodeMapping.hhaxPayers());
  const planOfCareSettingsQuery = useQuery(queries.evvDutyCodeMapping.planOfCareSettings());

  const [pickedPayerId, setPickedPayerId] = React.useState<PayerId | null>(null);
  const isPayerLocked = props.payerId !== undefined;
  const selectedPayerId = props.payerId ?? pickedPayerId;
  const setSelectedPayerId = React.useCallback(
    (payerId: PayerId | null) => {
      if (!isPayerLocked) {
        setPickedPayerId(payerId);
      }
    },
    [isPayerLocked]
  );
  const [selectedPlanOfCareTypeId, setSelectedPlanOfCareTypeId] =
    React.useState<PlanOfCareTypeId | null>(null);
  const [draftMappings, setDraftMappings] = React.useState<DraftHHAXMappingMap>({});
  const [savingPlanOfCareItemId, setSavingPlanOfCareItemId] =
    React.useState<PlanOfCareItemId | null>(null);

  const payers = React.useMemo(() => payersQuery.data?.payers ?? [], [payersQuery.data?.payers]);

  React.useEffect(() => {
    if (!isPayerLocked && pickedPayerId === null && payers.length > 0) {
      setPickedPayerId(payers[0].id);
    }
  }, [payers, pickedPayerId, isPayerLocked]);

  const hhaxItemsQuery = useQuery({
    ...queries.evvDutyCodeMapping.hhaxItems(selectedPayerId as PayerId),
    enabled: selectedPayerId !== null,
  });

  // Offices without a plan of care type have no items to map.
  const planOfCareTypes = React.useMemo(
    () =>
      (planOfCareSettingsQuery.data?.planOfCares ?? []).flatMap((planOfCare) =>
        planOfCare.planOfCareTypeId === null
          ? []
          : [{ ...planOfCare, planOfCareTypeId: planOfCare.planOfCareTypeId }]
      ),
    [planOfCareSettingsQuery.data?.planOfCares]
  );

  React.useEffect(() => {
    if (selectedPlanOfCareTypeId === null && planOfCareTypes.length > 0) {
      setSelectedPlanOfCareTypeId(planOfCareTypes[0].planOfCareTypeId);
    }
  }, [planOfCareTypes, selectedPlanOfCareTypeId]);

  const selectedPlanOfCareType =
    planOfCareTypes.find((x) => x.planOfCareTypeId === selectedPlanOfCareTypeId) ?? null;

  const columns = React.useMemo(
    () => [...(selectedPlanOfCareType?.columns ?? [])].sort(bySectionThenCode),
    [selectedPlanOfCareType]
  );

  const hhaxItemOptions: HHAXItemOption[] = React.useMemo(
    () =>
      (hhaxItemsQuery.data?.items ?? []).map((item) => ({
        value: item.id,
        label: `${item.taskCode} - ${item.taskName}`,
      })),
    [hhaxItemsQuery.data?.items]
  );

  // hhaxItemsMapping holds one entry per payer, so a row's saved value depends on the payer
  // currently selected.
  const mappedHhaxItemIdOf = React.useCallback(
    (column: PlanOfCareColumn): HHAXPocItemId | null =>
      column.hhaxItemsMapping?.find((mapping) => mapping.payerId === selectedPayerId)?.hhaxItemId ??
      null,
    [selectedPayerId]
  );

  const saveMutation = useMutation({
    mutationFn: (payload: {
      planOfCareItemId: PlanOfCareItemId;
      hhaxPOCItemId: HHAXPocItemId | null;
      payerId: PayerId;
      planOfCareTypeId: PlanOfCareTypeId;
    }) =>
      api.post("./hhax_medflyt_poc_mapping", {
        body: {
          payerId: payload.payerId,
          planOfCareTypeId: payload.planOfCareTypeId,
          medflytPOCItemId: payload.planOfCareItemId,
          hhaxPOCItemId: payload.hhaxPOCItemId,
        },
      }),
    onSuccess: (_data, variables) => {
      setDraftMappings((current) => {
        const next = { ...current };
        delete next[String(variables.planOfCareItemId)];
        return next;
      });

      queryClient.invalidateQueries({ queryKey: ["evv-duty-code-mapping"] });

      toast({ status: "success", title: "HHAeXchange task saved" });
    },
    onError: (error) => {
      toast({ status: "error", title: formatErrorResponse(error) });
    },
    onSettled: () => setSavingPlanOfCareItemId(null),
  });

  if (payersQuery.isLoading || planOfCareSettingsQuery.isLoading) {
    return <LoadingState label="Loading HHAeXchange mapping..." />;
  }

  if (payersQuery.isError) {
    return <ErrorState message={formatErrorResponse(payersQuery.error)} />;
  }

  if (planOfCareSettingsQuery.isError) {
    return <ErrorState message={formatErrorResponse(planOfCareSettingsQuery.error)} />;
  }

  if (payers.length === 0) {
    return <Text color="gray.500">No payers are integrated with HHAeXchange for this agency.</Text>;
  }

  if (isPayerLocked && !payers.some((payer) => payer.id === props.payerId)) {
    return <Text color="gray.500">This payer is not integrated with HHAeXchange.</Text>;
  }

  return (
    <Box>
      <Text color="gray.600" mb={4}>
        Map each Plan of Care item to the HHAeXchange task it reports as. Mapping is per payer, so
        the same item can report differently for each HHAeXchange payer.
      </Text>

      <Flex alignItems="center" flexWrap="wrap" gap={3} mb={4}>
        <Text mb={0}>Payer</Text>
        {isPayerLocked ? (
          <Text fontWeight="semibold" mb={0}>
            {payers.find((payer) => payer.id === selectedPayerId)?.name ?? String(selectedPayerId)}
          </Text>
        ) : (
          <Select
            searchable
            label="Payer"
            multiple={false}
            options={payers.map((payer) => ({ value: payer.id, label: payer.name }))}
            size="sm"
            value={selectedPayerId}
            width="260px"
            onChange={(value) => setSelectedPayerId(value ?? null)}
          />
        )}

        <Text mb={0} ml={4}>
          Office
        </Text>
        <Select
          searchable
          label="Office"
          multiple={false}
          options={planOfCareTypes.map((planOfCare) => ({
            value: planOfCare.planOfCareTypeId,
            label: planOfCare.officeName,
          }))}
          size="sm"
          value={selectedPlanOfCareTypeId}
          width="260px"
          onChange={(value) => setSelectedPlanOfCareTypeId(value ?? null)}
        />
      </Flex>

      {hhaxItemsQuery.isError ? (
        <ErrorState message={formatErrorResponse(hhaxItemsQuery.error)} />
      ) : (
        <MappingTableShell
          emptyMessage="No plan of care items for this office."
          externalColumnHeader="HHAeXchange Task"
          isEmpty={columns.length === 0}
        >
          {columns.map((column) => (
            <HhaxMappingRow
              key={String(column.id)}
              column={column}
              columns={columns}
              draftMappings={draftMappings}
              hhaxItemOptions={hhaxItemOptions}
              mappedHhaxItemIdOf={mappedHhaxItemIdOf}
              savingPlanOfCareItemId={savingPlanOfCareItemId}
              onChangeDraftMapping={(planOfCareItemId, value) =>
                setDraftMappings((current) => ({
                  ...current,
                  [String(planOfCareItemId)]: value ?? null,
                }))
              }
              onSave={(savedColumn, value) => {
                if (selectedPayerId === null || selectedPlanOfCareTypeId === null) {
                  return;
                }

                setSavingPlanOfCareItemId(savedColumn.id);
                saveMutation.mutate({
                  planOfCareItemId: savedColumn.id,
                  hhaxPOCItemId: value,
                  payerId: selectedPayerId,
                  planOfCareTypeId: selectedPlanOfCareTypeId,
                });
              }}
            />
          ))}
        </MappingTableShell>
      )}
    </Box>
  );
}
